import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { initializeApp, getApps } from "firebase/app";
import {
  GoogleAuthProvider,
  getAuth,
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signInWithEmailLink,
  signOut,
  type User,
} from "firebase/auth";
import { api } from "@/lib/api";
import { recaptchaToken } from "@/lib/recaptcha";

interface AuthValue {
  user: User | null;
  admin: boolean;
  loading: boolean;
  configured: boolean;
  error: string;
  signInWithGoogle: () => Promise<void>;
  requestEmailCode: (
    email: string,
  ) => Promise<{ request_id: string; message: string }>;
  verifyEmailCode: (
    email: string,
    requestId: string,
    code: string,
  ) => Promise<void>;
  signOutUser: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

function firebaseConfig() {
  const value = import.meta.env.VITE_FIREBASE_CONFIG;
  if (!value) return null;
  try {
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const config = firebaseConfig();
  const [user, setUser] = useState<User | null>(null);
  const [admin, setAdmin] = useState(false);
  const [loading, setLoading] = useState(Boolean(config));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!config) return;
    const app = getApps()[0] || initializeApp(config);
    const auth = getAuth(app);
    const unsubscribe = onAuthStateChanged(auth, (value) => {
      void (async () => {
        setUser(value);
        if (!value) {
          setAdmin(false);
          setLoading(false);
          return;
        }
        try {
          const token = await value.getIdTokenResult();
          setAdmin(
            token.claims.admin === true ||
              token.claims.hopehangar_admin === true,
          );
        } catch {
          setAdmin(false);
        } finally {
          setLoading(false);
        }
      })();
    });
    void getRedirectResult(auth).catch((caught: unknown) => {
      setError(
        caught instanceof Error
          ? caught.message
          : "Google sign-in could not be completed.",
      );
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      admin,
      loading,
      configured: Boolean(config),
      error,
      signInWithGoogle: async () => {
        setError("");
        const app = getApps()[0] || initializeApp(config!);
        const auth = getAuth(app);
        const provider = new GoogleAuthProvider();
        if (import.meta.env.VITE_FIREBASE_AUTH_MODE === "popup") {
          try {
            await signInWithPopup(auth, provider);
          } catch (caught: unknown) {
            setError(
              caught instanceof Error
                ? caught.message
                : "Google sign-in could not be completed.",
            );
          }
          return;
        }
        await signInWithRedirect(auth, provider);
      },
      requestEmailCode: async (email) => {
        setError("");
        try {
          const protectionToken = await recaptchaToken("admin_email_login");
          return await api<{ request_id: string; message: string }>(
            "/auth/email/verification",
            {
              method: "POST",
              body: JSON.stringify({
                email,
                recaptcha_token: protectionToken,
              }),
            },
          );
        } catch (caught: unknown) {
          const message =
            caught instanceof Error
              ? caught.message
              : "The sign-in code could not be sent.";
          setError(message);
          throw caught;
        }
      },
      verifyEmailCode: async (email, requestId, code) => {
        setError("");
        try {
          const result = await api<{ link: string }>(
            "/auth/email/verification",
            {
              method: "PUT",
              body: JSON.stringify({
                email,
                request_id: requestId,
                code,
              }),
            },
          );
          const app = getApps()[0] || initializeApp(config!);
          await signInWithEmailLink(getAuth(app), email, result.link);
        } catch (caught: unknown) {
          const message =
            caught instanceof Error
              ? caught.message
              : "The sign-in code could not be verified.";
          setError(message);
          throw caught;
        }
      },
      signOutUser: async () => {
        await signOut(getAuth());
      },
    }),
    [user, admin, loading, error, Boolean(config)],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
